import React from 'react';
import { nurseries, Nursery, calculateGreenScore, getEcologicalRating } from '../data/nurseries';
import '../styles/components/NurseriesSection.css';

interface NurseriesSectionProps {
  className?: string;
}

// Defining a type for our categories to ensure consistency
type PlantCategory = 'native' | 'invasive' | 'cultivar' | 'non-native-non-invasive';

const NurseriesSection: React.FC<NurseriesSectionProps> = ({ className = '' }) => {
  
  const getPlantCounts = (nursery: Nursery) => {
    // We use Record to tell TypeScript exactly what keys to expect
    const counts: Record<PlantCategory, number> = { 
      native: 0, 
      invasive: 0, 
      cultivar: 0, 
      'non-native-non-invasive': 0 
    };
    
    nursery.plantInventory.forEach(plant => {
      // Cast the category to our type to safely increment
      const cat = plant.category as PlantCategory;
      if (counts[cat] !== undefined) {
        counts[cat]++;
      }
    });
    return counts;
  };

  const getPlantPercentage = (nursery: Nursery, category: PlantCategory) => {
    const counts = getPlantCounts(nursery);
    const total = nursery.plantInventory.length;
    return total > 0 ? Math.round((counts[category] / total) * 100) : 0;
  };

  const renderPlantInventory = (nursery: Nursery) => {
    const inventory = nursery.plantInventory;
    const nativePlants = inventory.filter(plant => plant.category === 'native');
    const invasivePlants = inventory.filter(plant => plant.category === 'invasive');
    const cultivars = inventory.filter(plant => plant.category === 'cultivar');
    const nonNativePlants = inventory.filter(plant => plant.category === 'non-native-non-invasive');

    return (
      <div className="plant-inventory">
        <div className="inventory-summary">
          <div className="plant-category native">
            <span className="category-label">Native Plants</span>
            <span className="category-count">{nativePlants.length}</span>
            <span className="category-percentage">{getPlantPercentage(nursery, 'native')}%</span>
          </div>
          <div className="plant-category invasive">
            <span className="category-label">Invasive Plants</span>
            <span className="category-count">{invasivePlants.length}</span>
            <span className="category-percentage">{getPlantPercentage(nursery, 'invasive')}%</span>
          </div>
          <div className="plant-category cultivar">
            <span className="category-label">Cultivars</span>
            <span className="category-count">{cultivars.length}</span>
            <span className="category-percentage">{getPlantPercentage(nursery, 'cultivar')}%</span>
          </div>
          <div className="plant-category non-native">
            <span className="category-label">Non-Native (Safe)</span>
            <span className="category-count">{nonNativePlants.length}</span>
            <span className="category-percentage">{getPlantPercentage(nursery, 'non-native-non-invasive')}%</span>
          </div>
        </div>

        {/* NATIVES SECTION */}
        {nativePlants.length > 0 && (
          <div className="plant-list native-plants">
            <h4>Native Plants Available</h4>
            <div className="plant-grid">
              {nativePlants.slice(0, 6).map((plant, index) => (
                <div key={plant.plantId || `native-${index}`} className="plant-item">
                  <span className="plant-name">{plant.name}</span>
                  <span className="plant-scientific">{plant.scientificName}</span>
                  <span className="plant-availability">{plant.availability}</span>
                  <span className="plant-price">${plant.price}</span>
                </div>
              ))}
              {nativePlants.length > 6 && (
                <div className="plant-item more-plants">
                  +{nativePlants.length - 6} more native plants
                </div>
              )}
            </div>
          </div>
        )}

        {/* INVASIVES WARNING SECTION */}
        {invasivePlants.length > 0 && (
          <div className="plant-list invasive-plants">
            <h4>Invasive Plants (⚠️ Not Recommended)</h4>
            <div className="plant-grid">
              {invasivePlants.map((plant, index) => (
                <div key={plant.plantId || `invasive-${index}`} className="plant-item invasive">
                  <span className="plant-name">{plant.name}</span>
                  <span className="plant-scientific">{plant.scientificName}</span>
                  <span className="plant-warning">Invasive Species</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderEcologicalMetrics = (nursery: Nursery) => {
    const greenScore = calculateGreenScore(nursery);
    const ecologicalRating = getEcologicalRating(greenScore);
    const metrics = nursery.ecologicalMetrics;

    return (
      <div className="ecological-metrics">
        <div className="green-score-display">
          <div className="score-circle" style={{ backgroundColor: ecologicalRating.color }}>
            <span className="score-letter">{ecologicalRating.rating}</span>
            <span className="score-number">{greenScore}</span>
          </div>
          <div className="score-description">
            <h4>{ecologicalRating.description}</h4>
            <p>Overall ecological health rating</p>
          </div>
        </div>

        <div className="metrics-grid">
          <div className="metric-item">
            <span className="metric-label">Native Plants</span>
            <div className="metric-bar">
              <div className="metric-fill native" style={{ width: `${metrics.nativePlantPercentage}%` }}></div>
            </div>
            <span className="metric-value">{metrics.nativePlantPercentage}%</span>
          </div>

          <div className="metric-item">
            <span className="metric-label">Invasive Plants</span>
            <div className="metric-bar">
              <div className="metric-fill invasive" style={{ width: `${metrics.invasivePlantPercentage}%` }}></div>
            </div>
            <span className="metric-value">{metrics.invasivePlantPercentage}%</span>
          </div>

          <div className="metric-item">
            <span className="metric-label">Sustainable Practices</span>
            <div className="metric-tags">
              {metrics.sustainabilityPractices.map((practice) => (
                <span key={practice} className="practice-tag">{practice}</span>
              ))}
            </div>
          </div>

          <div className="metric-item">
            <span className="metric-label">Policies</span>
            <div className="policy-tags">
              {metrics.organicPractices && <span className="policy-tag organic">Organic</span>}
              {metrics.localSourcing && <span className="policy-tag local">Local Sourcing</span>}
              {metrics.educationalPrograms && <span className="policy-tag education">Education</span>}
              {metrics.nativePlantPromotion && <span className="policy-tag native-promotion">Promotes Natives</span>}
              {metrics.waterConservation && <span className="policy-tag water">Water Conservation</span>}
              <span className={`policy-tag invasive-${metrics.invasiveSpeciesPolicy}`}>
                Invasive Policy: {metrics.invasiveSpeciesPolicy}
              </span>
              <span className={`policy-tag pesticide-${metrics.pesticidePolicy}`}>
                Pesticides: {metrics.pesticidePolicy}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`nurseries-section ${className}`}>
      <div className="nurseries-header">
        <h2>Native Plant Nurseries</h2>
        <p className="nurseries-subtitle">
          Discover local nurseries and their ecological impact. Green ratings show how environmentally friendly each nursery is.
        </p>
      </div>

      <div className="nurseries-content">
        <div className="nurseries-grid">
          {nurseries.map((nursery) => {
            const greenScore = calculateGreenScore(nursery);
            const ecologicalRating = getEcologicalRating(greenScore);
            
            return (
              <div key={nursery.id} className="nursery-card">
                <div className="nursery-card-header">
                  <div className="nursery-info">
                    <h3 className="nursery-name">{nursery.name}</h3>
                    <div className="nursery-rating">
                      <span className="rating-stars">{'★'.repeat(Math.floor(nursery.rating))}</span>
                      <span className="rating-text">{nursery.rating} ({nursery.reviewCount} reviews)</span>
                    </div>
                  </div>
                  <div className="ecological-badge" style={{ backgroundColor: ecologicalRating.color }}>
                    <span className="badge-rating">{ecologicalRating.rating}</span>
                    <span className="badge-score">{greenScore}</span>
                  </div>
                </div>

                <div className="nursery-card-body">
                  <p className="nursery-description">{nursery.description}</p>
                  
                  <div className="nursery-contact">
                    <div className="contact-item">
                      <strong>Address:</strong> {nursery.address}
                    </div>
                    <div className="contact-item">
                      <strong>Phone:</strong> {nursery.phone}
                      {nursery.website && (
                        <span className="nursery-website">
                          | <a href={nursery.website} target="_blank" rel="noopener noreferrer">Website</a>
                        </span>
                      )}
                    </div>
                  </div>

                  {renderEcologicalMetrics(nursery)}
                  {renderPlantInventory(nursery)}
                </div>
              </div>
            );
          })}
        </div>

        {nurseries.length === 0 && (
          <div className="no-nurseries-message">
            <h3>Nursery Information Coming Soon</h3>
            <p>We're working on gathering comprehensive data about local nurseries and their plant inventories.</p>
          </div>
        )}
      </div>

      <div className="nurseries-cta">
        <div className="cta-card">
          <h3>Want to recommend a nursery?</h3>
          <button className="cta-button">Suggest a Nursery</button>
        </div>
      </div>
    </div>
  );
};

export default NurseriesSection;